import { useEffect } from 'react';
import { ARTICLE_JSON_LD_ID } from '../constants';

export const useArticleSeo = ({
  author,
  canonicalUrl,
  jsonLdOverride,
  keywords,
  publishedAt,
  seoDescription,
  seoImage,
  seoLocale,
  seoTitle,
  summary,
  title,
}) => {
  useEffect(() => {
    if (typeof document === 'undefined' || !seoTitle) {
      return undefined;
    }

    const managed = [];

    const setMetaTag = (attr, key, content) => {
      if (!content) {
        return;
      }

      const selector = `meta[${attr}="${key}"]`;
      const existing = document.head.querySelector(selector);
      if (existing) {
        managed.push({
          node: existing,
          prev: existing.getAttribute('content'),
          attr: 'content',
          created: false,
        });
        existing.setAttribute('content', content);
        return;
      }

      const meta = document.createElement('meta');
      meta.setAttribute(attr, key);
      meta.setAttribute('content', content);
      meta.setAttribute('data-article-meta', 'true');
      document.head.appendChild(meta);
      managed.push({ node: meta, created: true });
    };

    const setLinkTag = (rel, href) => {
      if (!href) {
        return;
      }

      const existing = document.head.querySelector(`link[rel="${rel}"]`);
      if (existing) {
        managed.push({
          node: existing,
          prev: existing.getAttribute('href'),
          attr: 'href',
          created: false,
        });
        existing.setAttribute('href', href);
        return;
      }

      const link = document.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      link.setAttribute('data-article-meta', 'true');
      document.head.appendChild(link);
      managed.push({ node: link, created: true });
    };

    const toAbsoluteUrl = (value) => {
      if (!value || typeof window === 'undefined') {
        return '';
      }
      if (value.startsWith('http://') || value.startsWith('https://')) {
        return value;
      }
      return `${window.location.origin}${value.startsWith('/') ? value : `/${value}`}`;
    };

    const absoluteImage = toAbsoluteUrl(seoImage);
    const absoluteCanonicalUrl = toAbsoluteUrl(canonicalUrl) || canonicalUrl;
    const keywordList = Array.isArray(keywords) ? keywords.filter(Boolean) : [];

    const previousTitle = document.title;
    document.title = seoTitle;

    setMetaTag('name', 'description', seoDescription);
    setMetaTag('name', 'keywords', keywordList.join(', '));
    setMetaTag('name', 'author', author);
    setMetaTag('property', 'og:title', seoTitle);
    setMetaTag('property', 'og:description', seoDescription);
    setMetaTag('property', 'og:type', 'article');
    setMetaTag('property', 'og:url', absoluteCanonicalUrl);
    setMetaTag('property', 'og:locale', seoLocale);
    if (publishedAt) {
      setMetaTag('property', 'article:published_time', publishedAt);
    }
    if (absoluteImage) {
      setMetaTag('property', 'og:image', absoluteImage);
    }
    setMetaTag('name', 'twitter:card', absoluteImage ? 'summary_large_image' : 'summary');
    setMetaTag('name', 'twitter:title', seoTitle);
    setMetaTag('name', 'twitter:description', seoDescription);
    if (absoluteImage) {
      setMetaTag('name', 'twitter:image', absoluteImage);
    }
    setLinkTag('canonical', absoluteCanonicalUrl);

    const baseJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: seoDescription || summary || '',
      author: {
        '@type': 'Person',
        name: author,
      },
      inLanguage: seoLocale,
      keywords: keywordList.join(', '),
    };

    const jsonLd = jsonLdOverride ? { ...baseJsonLd, ...jsonLdOverride } : baseJsonLd;

    if (absoluteCanonicalUrl) {
      jsonLd.mainEntityOfPage = absoluteCanonicalUrl;
    }
    if (publishedAt) {
      jsonLd.datePublished = publishedAt;
    }
    if (absoluteImage) {
      jsonLd.image = absoluteImage;
    }

    const existingScript = document.getElementById(ARTICLE_JSON_LD_ID);
    if (existingScript) {
      managed.push({
        node: existingScript,
        prev: existingScript.textContent,
        isScript: true,
        created: false,
      });
      existingScript.textContent = JSON.stringify(jsonLd);
    } else {
      const script = document.createElement('script');
      script.id = ARTICLE_JSON_LD_ID;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      script.setAttribute('data-article-meta', 'true');
      document.head.appendChild(script);
      managed.push({ node: script, created: true });
    }

    return () => {
      document.title = previousTitle;
      managed.forEach(({ node, prev, attr, created, isScript }) => {
        if (created) {
          node.remove();
        } else if (node && isScript) {
          node.textContent = prev || '';
        } else if (node && attr) {
          if (prev == null) {
            node.removeAttribute(attr);
          } else {
            node.setAttribute(attr, prev);
          }
        }
      });
    };
  }, [
    author,
    canonicalUrl,
    jsonLdOverride,
    keywords,
    publishedAt,
    seoDescription,
    seoImage,
    seoLocale,
    seoTitle,
    summary,
    title,
  ]);
};
